import React, { useEffect, useState } from "react";
import {
  StyledCreateButtonContainer,
  StyledLoadingContainer,
  StyledMessageContainer,
  StyledTabContainer,
  StyledTabsContainer,
} from "./Management.styled";
import { Tab } from "../../components/Tab/Tab";
import { PageNames, TabMenu } from "../../types/AppTypes";
import { ReactComponent as GraduationIcon } from "../../icons/graduation.svg";
import { ReactComponent as ArchivedIcon } from "../../icons/archived.svg";
import { ReactComponent as UsersIcon } from "../../icons/users.svg";
import {
  ACTIVE_RESOURCES_LABEL,
  ARCHIVING_LABEL,
  CM_SHEET_LABEL,
  CM_SHEETS_CREATION_ERROR_LABEL,
  CM_SHEETS_CREATION_IN_PROGRESS_LABEL,
  CONTACT_TECHNICAL_SUPPORT,
  CREATE_CM_SHEETS_LABEL,
  CREATE_CM_SHEETS_MESSAGE,
  ERROR_LABEL,
  ERROR_MESSAGE_LABEL,
  MANAGEMENT_LABEL,
} from "../../constants/Dictionary";
import { ManageCMSheet } from "./ManagementTabs/ManageCMSheet/ManageCMSheet";
import {
  CustomerErrorCodesEnum,
  StatusCodes,
  TabMenuManagement,
  ConfigMenuEnum,
  configMenuLabels,
} from "../../constants/AppConstants";
import {
  ExpandMenu,
  ExpandMenuOptionItem,
} from "../../components/ExpandMenu/ExpandMenu";
import { ManageActiveResources } from "./ManagementTabs/ManageActiveResources/ManageActiveResources";
import { ManageOrganizationChart } from "./ManagementTabs/ManageOrganizationChart/ManageOrganizationChart";
import { Button, ButtonSizes } from "../../components/core/Button/Button";
import { ReactComponent as PlusIcon } from "../../icons/add.svg";
import { PopupConfirm } from "../../components/PopupConfirm/PopupConfirm";
import {
  ConfigItem,
  ConfigStatusEnum,
} from "../../types/api/configurations.type";
import { MessageBox } from "../../components/MessageBox/MessageBox";
import { theme } from "../../constants/StyledComponents";
import { AxiosError } from "axios";
import { SingleErrorResponse } from "../../types/api/generic.type";
import { Loader } from "../../components/core/Loader/Loader";
import { useNotification } from "../../hooks/notification.hook";
import { useBreadCrumb } from "../../hooks/useBreadCrumbs.hooks";
import { useEditing } from "../../providers/editing.provider";
import { MenagementArchiving } from "./ManagementTabs/MenageArchiving/ManageArchiving";
import { useDispatch } from "react-redux";
import { useAppSelector } from "../../hooks/store.hook";
import { setTabsManagement } from "../../stores/actions/management.actions";
import {
  useCreateCmSheetConfig,
  useCmSheetConfigurations,
} from "../../hooks/management/useManagementCmSheet.hooks";

export const Management = () => {
  const dispatch = useDispatch();
  const { notification } = useNotification();
  const { confirmLeave, edit } = useEditing();

  const tabMenu: TabMenu[] = [
    {
      id: TabMenuManagement.ACTIVE_RESOURCES,
      label: ACTIVE_RESOURCES_LABEL,
      icon: <UsersIcon />,
    },
    {
      id: TabMenuManagement.CM_SHEET,
      label: CM_SHEET_LABEL,
      icon: <GraduationIcon />,
    },
    {
      id: TabMenuManagement.ARCHIVING,
      label: ARCHIVING_LABEL,
      icon: <ArchivedIcon />,
    },
  ];

  const { tabsManagement } = useAppSelector((state) => state.management);

  const getConfigMenu = (): ExpandMenuOptionItem<ConfigMenuEnum>[] => {
    const menuItems: ConfigMenuEnum[] = Object.values(ConfigMenuEnum);

    return menuItems.map((item) => ({
      value: item,
      label: configMenuLabels[item],
    }));
  };

  const [selectedMenu, setSelectedMenu] = useState<
    ExpandMenuOptionItem<ConfigMenuEnum>
  >(getConfigMenu()[0]);
  const [showConfirmConfigPopup, setShowConfirmConfigPopup] = useState(false);

  const [currentConfig, setCurrentConfig] = useState<ConfigItem | null>(null);

  const { config, isFetching } = useCmSheetConfigurations({
    enabled: tabsManagement === TabMenuManagement.CM_SHEET,
  });

  const { confirmConfig, isConfirmConfigLoading } = useCreateCmSheetConfig();

  const handleConfirmConfig = async () => {
    try {
      await confirmConfig();
    } catch (error) {
      const errorObj = error as AxiosError<SingleErrorResponse>;
      let errorMessageObj = {
        message: ERROR_LABEL,
        description: ERROR_MESSAGE_LABEL,
      };

      if (
        errorObj.response &&
        errorObj.response.status === StatusCodes.CUSTOM_ERROR
      ) {
        if (
          errorObj.response.data.code ===
          CustomerErrorCodesEnum.CMSHEETS_NOT_CLOSED
        ) {
          errorMessageObj = {
            message: CM_SHEETS_CREATION_ERROR_LABEL,
            description: errorObj.response.data.message,
          };
        }
      }
      console.error(error);
      notification.error(errorMessageObj.message, errorMessageObj.description);
    } finally {
      setShowConfirmConfigPopup(false);
    }
  };

  useEffect(() => {
    if (config && config.length > 0) {
      const currentConfig = config.find((item) => !item.confirmed);
      if (currentConfig) {
        setCurrentConfig(currentConfig);
      }
    }
  }, [config]);

  useBreadCrumb([
    {
      name: MANAGEMENT_LABEL,
      pathName: PageNames.MANAGEMENT,
    },
  ]);

  useEffect(() => {
    if (tabMenu.length > 0 && tabsManagement === null) {
      dispatch(setTabsManagement(TabMenuManagement.ACTIVE_RESOURCES));
    }
  }, [tabMenu, tabsManagement, dispatch]);

  return (
    <>
      <StyledTabsContainer>
        <Tab
          tabSelected={tabsManagement!}
          setTabSelected={(option) => dispatch(setTabsManagement(option))}
          tabMenu={tabMenu}
          confirmLeave={confirmLeave}
        >
          {tabsManagement === TabMenuManagement.ORGANIZATIONAL_CHART && (
            <StyledTabContainer>
              <ManageOrganizationChart />
            </StyledTabContainer>
          )}
          {tabsManagement === TabMenuManagement.CM_SHEET && (
            <StyledTabContainer $withExpandMenu>
              {!currentConfig ? (
                <StyledLoadingContainer>
                  <Loader loading={true} />
                </StyledLoadingContainer>
              ) : (
                <>
                  {(currentConfig.status === ConfigStatusEnum.RUNNING ||
                    currentConfig.status === ConfigStatusEnum.RUNNABLE) && (
                    <StyledMessageContainer>
                      <MessageBox
                        message={CM_SHEETS_CREATION_IN_PROGRESS_LABEL}
                      />
                    </StyledMessageContainer>
                  )}
                  {currentConfig.status === ConfigStatusEnum.IN_ERROR && (
                    <StyledMessageContainer>
                      <MessageBox
                        message={`${CM_SHEETS_CREATION_ERROR_LABEL}. ${CONTACT_TECHNICAL_SUPPORT}`}
                        color={theme.colors.red_1}
                      />
                    </StyledMessageContainer>
                  )}
                  {currentConfig.status === ConfigStatusEnum.IDLE &&
                    (isFetching ? (
                      <StyledLoadingContainer>
                        <Loader loading={true} />
                      </StyledLoadingContainer>
                    ) : (
                      <>
                        <ExpandMenu
                          data={getConfigMenu()}
                          menuSelected={selectedMenu}
                          setMenuSelected={setSelectedMenu}
                          confirmLeave={confirmLeave}
                        />
                        <ManageCMSheet
                          selectedMenu={selectedMenu.value}
                          configYear={currentConfig.year}
                        />
                      </>
                    ))}
                </>
              )}
            </StyledTabContainer>
          )}
          {tabsManagement === TabMenuManagement.ACTIVE_RESOURCES && (
            <StyledTabContainer>
              <ManageActiveResources />
            </StyledTabContainer>
          )}
          {tabsManagement === TabMenuManagement.ARCHIVING && (
            <StyledTabContainer>
              <MenagementArchiving />
            </StyledTabContainer>
          )}
        </Tab>
        {tabsManagement === TabMenuManagement.CM_SHEET && (
          <StyledCreateButtonContainer>
            <Button
              label={CREATE_CM_SHEETS_LABEL}
              size={ButtonSizes.SMALL}
              icon={<PlusIcon />}
              disabled={
                edit ||
                (currentConfig
                  ? currentConfig.status === ConfigStatusEnum.RUNNING ||
                    currentConfig.status === ConfigStatusEnum.IN_ERROR ||
                    currentConfig.status === ConfigStatusEnum.RUNNABLE
                  : false)
              }
              onClick={() => setShowConfirmConfigPopup(true)}
            />
          </StyledCreateButtonContainer>
        )}
      </StyledTabsContainer>
      <PopupConfirm
        isOpen={showConfirmConfigPopup}
        onCancel={() => setShowConfirmConfigPopup(false)}
        onConfirm={handleConfirmConfig}
        label={CREATE_CM_SHEETS_LABEL}
        isLoading={isConfirmConfigLoading}
        popupText={CREATE_CM_SHEETS_MESSAGE}
      />
    </>
  );
};
